import React, { memo, useCallback, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { theme } from '../../../../theme'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { IQDialog, Preloader } from '../../../common'
import { TGroupDetail } from '../../../../type'
import { API } from '../../../../api'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { Box, colors, List, ListItemButton, Stack } from '@mui/material'
import Button from '@mui/material/Button'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import { dateFormat, Format } from '../../../../utility/date-format'
import Tooltip from '@mui/material/Tooltip'
import { LocalStorageManager } from '../../../../utility/local-storage-manager'
import { PATH } from '../../../../utility/path'
import { PermissionKey, PermissionProvider } from '../../../../context'
// Direct file import, not the shared barrel: the barrel re-exports SearchPaginationGroupList,
// which imports this folder's index — a cycle that leaves exports undefined at eval time.
import { EditButton } from '../../../shared/actions/EditButton'

type TProps = {
  label: string | React.ReactNode
  searchableText: string
  setSearchableText: (searchableText: string) => void
  disabled?: boolean
  labelColor?: string
}

const MEMBERSHIP_CACHE_HISTORY = 'membership-cache-history'

// eslint-disable-next-line react/display-name
export const MembershipGroupNameDialogButton = memo(
  ({ label, searchableText, setSearchableText, disabled = false, labelColor }: TProps) => {
    const handleButtonClick = () => {
      setSearchableText(searchableText)
    }

    return (
      <button
        type={'button'}
        onClick={handleButtonClick}
        style={{
          wordBreak: 'break-word',
          textDecoration: 'underline',
          textDecorationColor: theme.colors.primary500,
          gap: '4px',
          color: disabled ? theme.colors.base500 : labelColor ? labelColor : colors.common.black,
          backgroundColor: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: '16px',
          textAlign: 'left',
          width: '100%',
          paddingLeft: 0,
        }}
      >
        {label}{' '}
        <span style={{ position: 'relative' }}>
          <OpenInNewIcon style={{ color: theme.colors.primary500, fontSize: '16px', position: 'absolute', left: 4, bottom: 0 }} />
        </span>
      </button>
    )
  },
)

interface IProps {
  isShow: boolean
  title: string
  setTitle: (title: string) => void
  searchableText: string
  setSearchableText: (searchableText: string) => void
  onCancel: () => void
  from?: string
  returnType?: string
}

// eslint-disable-next-line react/display-name
export const MembershipGroupNameDialogView = memo(
  ({ title, isShow, searchableText, setSearchableText, setTitle, onCancel, from, returnType }: IProps) => {
    const navigation = useNavigate()
    const location = useLocation()
    const currentSearchParams = new URLSearchParams(location.search)
    const effectiveFrom = currentSearchParams.get('from') || from
    const effectiveReturnType = currentSearchParams.get('returnType') || returnType
    const currentTab = currentSearchParams.get('returnTab') || currentSearchParams.get('type')
    const [isLoading, setIsLoading] = useState(false)
    const [groupDetail, setGroupDetail] = useState<TGroupDetail>({} as TGroupDetail)
    const [searchHistory, setSearchHistory] = useState<{ DisplayName: string; Name: string }[]>([])
    if (searchHistory.length === 0 && searchableText) {
      setSearchHistory([{ DisplayName: title, Name: searchableText }])
    }

    const handleOnCancel = () => {
      setSearchHistory([])
      setSearchableText('')
      setTitle('')
      LocalStorageManager.clearPrefix(MEMBERSHIP_CACHE_HISTORY)
      onCancel()
    }

    const handleSetSearchableText = (item: { Name: string; DisplayName?: string }) => {
      setGroupDetail({} as TGroupDetail)
      setSearchHistory((prevHistory) => {
        const newItem = {
          Name: item.Name,
          DisplayName: item.DisplayName || item.Name,
        }
        return [...prevHistory, newItem]
      })
      setSearchableText(item.Name)
      setTitle(item.DisplayName || item.Name)
    }

    const getGroupDetail = useCallback(async () => {
      if (searchableText) {
        setIsLoading(true)
        const cacheKey = `${MEMBERSHIP_CACHE_HISTORY}${searchableText}`
        let detail = LocalStorageManager.getItem<TGroupDetail>(cacheKey)
        try {
          if (!detail) {
            detail = await API.ADManagement.group.all_details(searchableText)
            const cacheKey = `${MEMBERSHIP_CACHE_HISTORY}${searchableText}`
            LocalStorageManager.addItem(cacheKey, detail)
          }
          setGroupDetail(detail)
        } finally {
          setIsLoading(false)
        }
      }
    }, [searchableText])

    useEffect(() => {
      if (isShow) {
        void getGroupDetail()
      }
    }, [getGroupDetail, isShow])

    const handleBackClick = () => {
      if (searchHistory.length > 1) {
        const newHistory = [...searchHistory]
        newHistory.pop()
        setSearchHistory(newHistory)
        const lastSearch = newHistory[newHistory.length - 1]
        setSearchableText(lastSearch.Name)
        LocalStorageManager.removeItem(`${MEMBERSHIP_CACHE_HISTORY}${searchHistory[searchHistory.length - 1].Name}`)
      }
    }

    const renderPreloader = () => (
      <Preloader
        size='sm'
        style={{ display: 'block', margin: '0 auto' }}
      />
    )

    const renderCell = (title: string, value: string | number, testId: string) => (
      <Grid
        item
        sx={{ mb: 1 }}
      >
        <Typography
          color={colors.grey['600']}
          fontWeight={500}
        >
          {title}
        </Typography>
        <Typography
          color={colors.common.black}
          style={{ wordBreak: 'break-word' }}
          data-testid={testId}
        >
          {value}
        </Typography>
      </Grid>
    )

    const renderContent = () => {
      const renderMemberOfList = () => (
        <Grid
          spacing={2}
          xs={6}
          item
          direction='column'
          sx={{ pl: 2 }}
          container
        >
          <Typography
            color={colors.grey['600']}
            fontWeight={500}
          >
            Member of:
          </Typography>
          <List>
            {groupDetail.MemberOf?.map((member, index) => (
              <ListItemButton
                key={`membership-group-of-detail-${index}`}
                sx={{ px: 0, py: 0.5 }}
              >
                <MembershipGroupNameDialogButton
                  label={member.DisplayName || member.Name}
                  searchableText={member.Name}
                  setSearchableText={() =>
                    handleSetSearchableText({
                      Name: member.Name,
                      DisplayName: member.DisplayName,
                    })
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Grid>
      )

      const renderMemberList = () => (
        <Grid
          spacing={2}
          item
          xs={6}
          direction='column'
          container
        >
          <Typography
            color={colors.grey['600']}
            fontWeight={500}
          >
            Members:
          </Typography>
          <List>
            {groupDetail.Members?.map((member, index) => (
              <ListItemButton
                key={`membership-group-detail-${index}`}
                sx={{ px: 0, py: 0.5 }}
              >
                <MembershipGroupNameDialogButton
                  label={member.DisplayName || member.Name}
                  searchableText={member.Name}
                  setSearchableText={() =>
                    handleSetSearchableText({
                      Name: member.Name,
                      DisplayName: member.DisplayName,
                    })
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </Grid>
      )

      return (
        <Grid
          container
          spacing={1}
          sx={{
            pt: 0,
          }}
          direction={'column'}
        >
          {renderCell('Description:', groupDetail.Description, 'membership-group-description')}
          <Grid
            sx={{
              p: 3,
            }}
            container
            justifyContent='space-between'
            direction='row'
            spacing={2}
          >
            {renderMemberList()}
            {renderMemberOfList()}
          </Grid>
        </Grid>
      )
    }

    const renderActions = () => (
      <Grid
        container
        justifyContent='space-between'
        alignItems='center'
        sx={{ bg: colors.grey['100'] }}
        spacing={2}
      >
        <Grid item>
          {groupDetail.WhenChanged && (
            <Stack direction={'column'}>
              <Typography
                color={colors.grey['600']}
                fontWeight={500}
              >
                Updated At:
              </Typography>
              <Typography
                variant={'body2'}
                color={colors.common.black}
              >
                {groupDetail.WhenChanged && dateFormat(groupDetail.WhenChanged, Format['YYYY-MM-DD HH:MM:SS'])}
              </Typography>
            </Stack>
          )}
        </Grid>
        <Stack
          direction={'row'}
          alignItems='flex-end'
          spacing={2}
          sx={{ mt: 2 }}
        >
          <Tooltip
            arrow
            sx={{
              backgroundColor: 'red',
            }}
            title={
              searchHistory.length > 1
                ? searchHistory[searchHistory.length - 2].DisplayName || searchHistory[searchHistory.length - 2].Name
                : 'No more history'
            }
          >
            <span>
              <Button
                variant='outlined'
                disabled={searchHistory.length <= 1}
                onClick={handleBackClick}
                startIcon={<ArrowBackIosIcon />}
                sx={{ color: colors.blue, textAlign: 'left' }}
              >
                BACK
              </Button>
            </span>
          </Tooltip>
          <Button
            variant='contained'
            onClick={handleOnCancel}
            data-testid='CE-ok'
          >
            OK
          </Button>
        </Stack>
      </Grid>
    )

    return (
      <IQDialog
        highlightedActions
        size='md'
        isShow={isShow}
        onClose={handleOnCancel}
        title={
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {title ?? searchableText}
              {(groupDetail.Location === 'INT Memberships' || groupDetail.Location === 'EXT Memberships') && (
                <PermissionProvider
                  permission={PermissionKey.btns_memberships}
                  className='d-ib'
                >
                  <EditButton
                    onClick={() => {
                      const membershipEditType = groupDetail.Location === 'INT Memberships' ? 'internal' : 'external'
                      const params = new URLSearchParams({ type: membershipEditType })
                      if (effectiveFrom) {
                        params.set('from', effectiveFrom)
                        if (effectiveReturnType) params.set('returnType', effectiveReturnType)
                        if (currentTab) params.set('returnTab', currentTab)
                      }
                      handleOnCancel()
                      navigation(`${PATH.admin.ad_management.membership}/${groupDetail.GroupName}?${params.toString()}`)
                    }}
                    dataTestId='membership-group-edit'
                  />
                </PermissionProvider>
              )}
            </Box>
            {renderCell(groupDetail.Location ? `Location: ${groupDetail.Location}` : '', '', 'membership-group-location')}
          </>
        }
        content={isLoading ? renderPreloader() : renderContent()}
        actions={renderActions()}
        testPrefix='membership-group-view'
      />
    )
  },
)
